import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { auth, getErrorMessage } from '../../utils/api';
import { validatePassword, passwordChecklist } from '../../utils/passwordPolicy';
import { toast } from 'sonner';
import { KeyRound, LogOut, ShieldCheck, Check, X } from 'lucide-react';

/**
 * Forced password-reset screen.
 *
 * Shown immediately after login when the user's `must_reset_password` flag is
 * true (set by the corporate admin or team-invite flow when issuing a
 * system-generated temporary password). Blocks access to every other page
 * until the user picks a new policy-compliant password.
 */
export default function ForcePasswordChange({ user, onSuccess, onLogout }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const email = user?.email || '';
  const checklist = passwordChecklist(newPassword, { email });
  const localError = validatePassword(newPassword, { email });
  const matchError =
    confirmPassword && newPassword !== confirmPassword ? 'Passwords do not match' : null;
  const formError = localError || matchError;

  const submit = async (e) => {
    e.preventDefault();
    if (formError) {
      toast.error(formError);
      return;
    }
    setSaving(true);
    try {
      await auth.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success('Password updated');
      onSuccess && onSuccess();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not change password'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-md border-0 shadow-xl" data-testid="force-password-change-card">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-3">
            <KeyRound className="w-6 h-6 text-blue-600" />
          </div>
          <CardTitle className="text-2xl">Set Your Password</CardTitle>
          <p className="text-sm text-gray-500 mt-2">
            Welcome to DealShaq. You're signed in as <strong>{email}</strong>.
            For security, please replace your temporary password before continuing.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4" autoComplete="off">
            {/* Username anchor for password managers. Must NOT be display:none
                — Chrome's autofill engine ignores hidden inputs when deciding
                which credential to update. Visually-hidden + readOnly +
                tabIndex=-1 keeps it off-screen and out of focus order, but
                Chrome still sees it and binds the new password to the
                correct account email. */}
            <input
              type="email"
              name="username"
              value={email}
              autoComplete="username"
              readOnly
              tabIndex={-1}
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: '-9999px',
                width: '1px',
                height: '1px',
                opacity: 0,
                pointerEvents: 'none',
              }}
            />
            <div>
              <Label htmlFor="fp-current">Temporary password</Label>
              <Input
                id="fp-current"
                data-testid="force-password-current"
                type="password"
                autoComplete="one-time-code"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="From your welcome email"
                required
              />
            </div>
            <div>
              <Label htmlFor="fp-new">New password</Label>
              <Input
                id="fp-new"
                data-testid="force-password-new"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Pick a strong one"
                required
              />
            </div>

            {/* Live policy checklist */}
            <ul className="text-xs space-y-1 bg-gray-50 rounded-md p-3" data-testid="password-policy-list">
              {checklist.map((rule) => (
                <li
                  key={rule.label}
                  className={`flex items-start gap-2 ${rule.ok ? 'text-emerald-700' : 'text-gray-500'}`}
                >
                  {rule.ok ? (
                    <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  ) : (
                    <X className="w-3.5 h-3.5 mt-0.5 shrink-0 text-gray-400" />
                  )}
                  <span>{rule.label}</span>
                </li>
              ))}
              <li className="flex items-start gap-2 text-gray-500 italic">
                <span className="w-3.5 h-3.5" />
                <span>Must not match your previous password (checked on save)</span>
              </li>
            </ul>

            <div>
              <Label htmlFor="fp-confirm">Confirm new password</Label>
              <Input
                id="fp-confirm"
                data-testid="force-password-confirm"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Type it again"
                required
              />
              {matchError && (
                <p className="text-xs text-red-600 mt-1" data-testid="force-password-match-error">
                  {matchError}
                </p>
              )}
            </div>

            <Button
              type="submit"
              data-testid="force-password-submit"
              disabled={saving || !!formError || !currentPassword}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <ShieldCheck className="w-4 h-4 mr-2" />
              {saving ? 'Updating…' : 'Set new password & continue'}
            </Button>
            <button
              type="button"
              data-testid="force-password-logout"
              onClick={onLogout}
              className="w-full text-sm text-gray-500 hover:text-gray-700 flex items-center justify-center gap-1 mt-2"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out instead
            </button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
